import {Presenter} from '../../../Support/with_presenter';
import {addDays, addWeeks, endOfMonth, startOfMonth, startOfWeek, subDays, subWeeks} from 'date-fns';
import {loadingResponse, NetworkRequestStatus, NetworkResult} from '../../../Network/NetworkResult';
import {CalendarEvent} from '../../../Models/CalendarEvent';
import {EventsInteractor} from '../../../Calendar/EventsInteractor';
import {map} from 'rxjs';
import {FullCrew} from '../../../Models/Crew';
import {MeService} from '../../../Network/MeService';
import {Me} from '../../../Models/Me';
import {WorkoutsService} from '../../../Network/WorkoutsService';
import {PartialWorkout} from '../../../Models/PartialWorkout';
import {CompositeSubscription} from '../../Support/CompositeSubscription';
import {fromPromise} from 'rxjs/internal/observable/innerFrom';

export enum ScreenType {
    Index,
    Create,
    View,
    Edit,
}

export enum CalendarMode {
    Month = 'month',
    Rsvp = 'rsvp',
    Week = 'week',
}

export interface IndexScreen {
    type: ScreenType.Index;
}

export interface CreateScreen {
    type: ScreenType.Create;
    date: Date | null;
}

export interface UpdateScreen {
    type: ScreenType.Edit;
    calendarEvent: CalendarEvent;
    updateDateTo?: Date;
}

export interface ViewScreen {
    type: ScreenType.View;
    calendarEvent: CalendarEvent;
}

export type Screen = IndexScreen | CreateScreen | UpdateScreen | ViewScreen;

export class EventCalendarPresenter implements Presenter {
    public screen: Screen = {type: ScreenType.Index};

    public me: NetworkResult<Me> = loadingResponse();
    public workouts: NetworkResult<PartialWorkout[]> = loadingResponse();

    private _mode: CalendarMode = CalendarMode.Month;
    private subscriptions = new CompositeSubscription();

    public get mode(): CalendarMode {
        return this._mode;
    }

    public set mode(value: CalendarMode) {
        this._mode = value;
        this.fetch();
    }

    private _referenceDate: Date = new Date();
    get referenceDate(): Date {
        return this._referenceDate;
    }

    set referenceDate(value: Date) {
        this._referenceDate = value;
        this.fetch();
    }

    public calendarEvents: NetworkResult<CalendarEvent[]> = loadingResponse();

    private get visibleRange(): [Date, Date] {
        if (this.mode === CalendarMode.Week) {
            const startAt = startOfWeek(this.referenceDate, {weekStartsOn: 1});
            return [startAt, addWeeks(startAt, 1)];
        }

        return [startOfMonth(this.referenceDate), endOfMonth(this.referenceDate)];
    }

    constructor(
        private crews: FullCrew[],
        private workoutsService: WorkoutsService,
        private eventInteractor: EventsInteractor,
        private meService: MeService,
    ) {}

    public async mount(): Promise<void> {
        this.me = await this.meService.get();
        await this.fetch();
    }

    public unmount(): void {
        this.subscriptions.clear();
    }

    private async fetch() {
        this.calendarEvents = loadingResponse();
        this.workouts = loadingResponse();
        //Query the events for the visible range with a buffer of one day on each side to make sure we get all events independent of timezone
        this.subscriptions.clear();
        this.subscriptions.add(
            this.eventInteractor
                .get(subDays(this.visibleRange[0], 1), addDays(this.visibleRange[1], 1))
                .pipe(
                    map((events) => {
                        if (events.status === NetworkRequestStatus.Success) {
                            return {
                                ...events,
                                data: events.data.filter((event) => {
                                    return this.crews.some((crew) => crew.id === event.crewId);
                                }),
                            };
                        }
                        return events;
                    }),
                )
                .subscribe((events) => {
                    this.calendarEvents = events;
                }),
        );

        this.subscriptions.add(
            fromPromise(this.workoutsService.between(this.visibleRange[0], this.visibleRange[1])).subscribe(
                (workouts) => {
                    this.workouts = workouts;
                },
            ),
        );
    }

    public onCalendarEventClick(calendarEvent: CalendarEvent) {
        this.screen = {type: ScreenType.View, calendarEvent: calendarEvent};
    }

    public onCalendarEventDateChange(calendarEvent: CalendarEvent, date: Date) {
        this.screen = {type: ScreenType.Edit, calendarEvent: calendarEvent, updateDateTo: date};
    }

    public onCreateClick(referenceDate: Date | null) {
        this.screen = {type: ScreenType.Create, date: referenceDate === null ? this.referenceDate : referenceDate};
    }

    public onFinishCreate() {
        this.screen = {type: ScreenType.Index};
        this.fetch();
    }

    public navigateBack() {
        if (this._mode === CalendarMode.Week) {
            this.onReferenceDateChange(subWeeks(this.referenceDate, 1));
        } else {
            const newDate = new Date(this.referenceDate);
            newDate.setMonth(newDate.getMonth() - 1);
            this.onReferenceDateChange(newDate);
        }
    }

    public navigateForward() {
        if (this.mode === CalendarMode.Week) {
            this.onReferenceDateChange(addWeeks(this.referenceDate, 1));
        } else {
            const newDate = new Date(this.referenceDate);
            newDate.setMonth(newDate.getMonth() + 1);
            this.onReferenceDateChange(newDate);
        }
    }

    public navigateToday() {
        this.onReferenceDateChange(new Date());
    }

    public onReferenceDateChange(date: Date) {
        this.referenceDate = date;
    }

    public onModeChange(mode: CalendarMode) {
        this.mode = mode;
    }

    public onRequestCloseModal() {
        this.screen = {type: ScreenType.Index};
    }

    public onRequestEdit(calendarEvent: CalendarEvent) {
        this.screen = {type: ScreenType.Edit, calendarEvent: calendarEvent};
    }
}
